import { clsx } from 'clsx';

import type { ChartV2PriceDisplay } from '../types/chart-v2.types';
import styles from './PriceDisplayToggle.module.scss';

/** Localisable strings for `<PriceDisplayToggle>`. */
export interface PriceDisplayToggleLocales {
  /** Label for the `price` option. Defaults to `"Price / Yield"`. */
  price?: string;
  /** Label for the `indexed` option. Defaults to `"Indexed to 100"`. */
  indexed?: string;
}

/**
 * Props for `<PriceDisplayToggle>`.
 *
 * Required: `value`, `onChange`. Always controlled — the host owns the
 * pane's price-display mode.
 */
export interface PriceDisplayToggleProps {
  /** Current price-display mode. */
  value: ChartV2PriceDisplay;
  /** Called with the new mode when the user picks the other option. */
  onChange: (next: ChartV2PriceDisplay) => void;
  /** Label overrides — see `PriceDisplayToggleLocales`. */
  locales?: PriceDisplayToggleLocales;
  /** Extra class name forwarded to the toggle root. */
  className?: string;
  /** Test selector forwarded as `data-testid`. */
  'data-testid'?: string;
}

/**
 * Two-option segmented toggle for switching a chartv2 pane between
 * absolute price (`'price'`) and indexed-to-100 (`'indexed'`) display.
 *
 * Always controlled — the host owns the pane's price-display mode.
 * In indexed mode, chartv2 rebases each visible series to its first
 * visible value (as %) and collapses to a shared y-axis.
 */
export const PriceDisplayToggle = ({
  value,
  onChange,
  locales,
  className,
  'data-testid': testId = 'chartv2-price-display-toggle',
}: PriceDisplayToggleProps) => {
  const options: Array<{ id: ChartV2PriceDisplay; label: string }> = [
    { id: 'price', label: locales?.price ?? 'Price / Yield' },
    { id: 'indexed', label: locales?.indexed ?? 'Indexed to 100' },
  ];

  return (
    <div
      className={clsx(styles.root, className)}
      role="group"
      data-testid={testId}
    >
      {options.map(option => {
        const active = option.id === value;

        return (
          <button
            key={option.id}
            type="button"
            className={clsx(styles.option, active && styles.active)}
            aria-pressed={active}
            onClick={() => {
              if (!active) {
                onChange(option.id);
              }
            }}
            data-testid={`${testId}-${option.id}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};
